import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Monitor, 
  Users, 
  Shield, 
  Zap, 
  Star, 
  ChevronDown, 
  ChevronUp, 
  Phone, 
  Mail, 
  MapPin,
  CheckCircle,
  ArrowRight,
  Play,
  Target,
  TrendingUp,
  DollarSign,
  Clock,
  Award,
  Briefcase,
  Building,
  Heart,
  Brain,
  Activity,
  Stethoscope,
  UserCheck,
  MessageSquare,
  FileText,
  BarChart3,
  Globe,
  Rocket,
  Scale,
  User,
  Sparkles
} from 'lucide-react';
import './App.css';
import ROICalculator from './ROICalculator';

const TeleTurnKeyPartnership = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [isVisible, setIsVisible] = useState({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[id]').forEach((el) => {
      observer.observe(el);
    });

    // Right-click protection
    const handleRightClick = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable F12, Ctrl+Shift+I, Ctrl+U, Ctrl+S
    const handleKeyDown = (e) => {
      if (
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && e.key === 'I') ||
        (e.ctrlKey && e.key === 'u') ||
        (e.ctrlKey && e.key === 's')
      ) {
        e.preventDefault();
        return false;
      }
    };

    document.addEventListener('contextmenu', handleRightClick);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      observer.disconnect();
      document.removeEventListener('contextmenu', handleRightClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: "easeOut" }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const includedFeatures = [
    {
      icon: Monitor,
      title: "Fully Developed Telehealth Platform",
      description: "Complete branded platform with your logo, colors, and messaging"
    },
    {
      icon: Users,
      title: "Licensed Providers & Infrastructure",
      description: "Operational team of doctors and nurse practitioners ready to serve patients"
    },
    {
      icon: FileText,
      title: "EHR, Intake Forms & Workflows",
      description: "Complete patient management system with HIPAA-compliant documentation"
    },
    {
      icon: Building,
      title: "Pharmacy + Lab Fulfillment Network",
      description: "Nationwide prescription and lab testing partnerships already established"
    },
    {
      icon: Rocket,
      title: "Launch & Scale Support",
      description: "Dedicated team to help you launch and grow your telehealth business"
    },
    {
      icon: BarChart3,
      title: "Marketing Assets & Playbooks",
      description: "Proven marketing materials and advertising frameworks that convert"
    }
  ];

  const services = [
    {
      icon: Scale,
      title: "Weight Loss",
      description: "GLP-1 medications and comprehensive weight management programs",
      color: "from-green-500 to-emerald-600"
    },
    {
      icon: UserCheck,
      title: "Men's Health",
      description: "Testosterone therapy, ED treatments, and male wellness programs",
      color: "from-blue-500 to-cyan-600"
    },
    {
      icon: Heart,
      title: "Hormone Therapy",
      description: "Bioidentical hormone replacement for both men and women",
      color: "from-pink-500 to-rose-600"
    },
    {
      icon: Brain,
      title: "Mental Health",
      description: "Anxiety, depression, and ADHD treatment with licensed therapists",
      color: "from-purple-500 to-violet-600"
    },
    {
      icon: User,
      title: "Hair Loss Solutions",
      description: "FDA-approved treatments for male and female pattern baldness",
      color: "from-orange-500 to-amber-600"
    },
    {
      icon: Sparkles,
      title: "Skin & Anti-Aging",
      description: "Dermatology consultations and anti-aging treatment programs",
      color: "from-teal-500 to-cyan-600"
    }
  ];

  const targetAudience = [
    {
      icon: Briefcase,
      title: "Entrepreneurs",
      description: "Seeking a semi-absentee business model in healthcare"
    },
    {
      icon: DollarSign,
      title: "Investors",
      description: "Looking for monthly cash flow in a booming sector"
    },
    {
      icon: Award,
      title: "Medical Professionals",
      description: "Wanting to launch their own clinic fast"
    },
    {
      icon: Target,
      title: "Marketers",
      description: "Ready to scale a modern, recession-proof offer"
    }
  ];

  const testimonials = [
    {
      name: "Karla R.",
      role: "Founder of WeightWellnessMD",
      content: "TeleTurn Key helped me get my clinic live in 30 days. I've already closed my first 20 patients.",
      avatar: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=400&fit=crop&crop=face"
    },
    {
      name: "James M.",
      role: "Digital Health Entrepreneur",
      content: "I wanted in on telehealth, but didn't know how. Their plug-and-play model made it possible.",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop&crop=face"
    },
    {
      name: "Dr. Sarah Chen",
      role: "Telehealth Clinic Owner",
      content: "From zero to $50K monthly revenue in 6 months. The support team makes all the difference.",
      avatar: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=400&h=400&fit=crop&crop=face"
    }
  ];

  const marketStats = [
    { number: "$300B+", label: "Telehealth Market Size" },
    { number: "85%", label: "Patient Satisfaction Rate" },
    { number: "3x", label: "Higher Profit Margins" },
    { number: "24/7", label: "Business Operations" }
  ];

  const faqs = [
    {
      question: "Do I need to be a doctor to run this?",
      answer: "No medical degree required! We provide all the medical staffing and fulfillment. You focus on business growth while we handle the clinical operations."
    },
    {
      question: "What's the upfront investment?",
      answer: "Much less than hiring a single provider or building a clinic from scratch. Investment varies based on your chosen services and market size. Apply to get detailed investment information."
    },
    {
      question: "How quickly can I launch my clinic?",
      answer: "Most partners are live within 30-60 days*. We handle all licensing, provider credentialing, and platform setup while you focus on your launch strategy."
    },
    {
      question: "What ongoing support do I receive?",
      answer: "Complete business support including marketing guidance, operational assistance, compliance updates, and access to our fulfillment team 24/7."
    },
    {
      question: "Do I own my brand and customer relationships?",
      answer: "Absolutely! This is your business and your brand. You own all patient relationships and control your revenue streams. We're your behind-the-scenes operations partner."
    },
    {
      question: "What makes this different from other telehealth opportunities?",
      answer: "We provide a complete turnkey business - not just software. You get operational infrastructure, licensed providers, fulfillment networks, and proven marketing systems from day one."
    }
  ];

  const handleCalendlyLink = () => {
    // J Cardenas - TeleTurn Key Partnership Consultation
    window.open('https://calendly.com/contact-teleturnkey/teleturn-key-partnership-consultation', '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100" style={{userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none', msUserSelect: 'none'}}>
      {/* Header */}
      <header className="relative z-50 bg-white/80 backdrop-blur-md border-b border-slate-200/50 sticky top-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <motion.div 
              className="flex items-center space-x-3"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Updated Logo with TTK Design */}
              <div className="flex items-center space-x-2">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg">
                  <span className="text-white font-bold text-lg">TTK</span>
                </div>
                <span className="text-2xl font-bold bg-gradient-to-r from-teal-600 to-teal-700 bg-clip-text text-transparent">
                  TeleTurn Key
                </span>
              </div>
            </motion.div>
            <motion.button
              onClick={handleCalendlyLink}
              className="bg-gradient-to-r from-teal-600 to-teal-700 text-white px-6 py-2 rounded-full font-semibold hover:from-teal-700 hover:to-teal-800 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Apply to Partner
            </motion.button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxoZWFsdGhjYXJlJTIwdGVjaG5vbG9neXxlbnwwfHx8fDE3NTIxODQzMDZ8MA&ixlib=rb-4.1.0&q=85')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-teal-900/90 to-teal-800/80" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={isVisible.hero ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1, ease: "easeOut" }}
          >
            <div className="inline-flex items-center space-x-2 bg-teal-100 text-teal-800 px-4 py-2 rounded-full font-semibold mb-6">
              <Award className="w-5 h-5" />
              <span>Fully Licensed • Ready-to-Scale • No Medical Degree Required</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
              Launch a Licensed &
              <span className="block bg-gradient-to-r from-teal-200 to-white bg-clip-text text-transparent">
                Compliant Telehealth Clinic
              </span>
              <span className="block text-4xl md:text-5xl text-teal-200">
                Efficiently!
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-teal-100 mb-8 max-w-4xl mx-auto leading-relaxed">
              Cash-pay. Compliant. Scalable. A done-for-you medical brand with clinical operations, 
              nationwide coverage, and high-margin services — ready for you to grow.
            </p>
            <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-6 mb-8">
              <motion.button
                onClick={handleCalendlyLink}
                className="bg-gradient-to-r from-white to-teal-50 text-teal-700 px-10 py-5 rounded-full font-bold text-xl hover:from-teal-50 hover:to-white transform hover:scale-105 transition-all duration-300 shadow-2xl hover:shadow-3xl flex items-center space-x-3"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Rocket className="w-6 h-6" />
                <span>Apply to Partner with TeleTurn Key</span>
              </motion.button>
            </div>
            <p className="text-teal-200 text-lg italic">
              "You bring the vision. We bring the clinic, staff, and systems."
            </p>
          </motion.div>
        </div>
      </section>

      {/* Problem/Solution Section with Updated Styling */}
      <section id="problem" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -60 }}
              animate={isVisible.problem ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Tired of Trying to
                <span className="block bg-gradient-to-r from-teal-600 to-teal-700 bg-clip-text text-transparent">
                  Build From Scratch?
                </span>
              </h2>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Most people waste months (or years) trying to get a telehealth business licensed, 
                compliant, and staffed. TeleTurn Key eliminates the friction — giving you everything 
                you need to launch a fully licensed telehealth clinic from day one.
              </p>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-6 h-6 text-teal-600" />
                  <span className="text-lg font-semibold text-gray-900">Clinical coverage in all 50 states</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-6 h-6 text-teal-600" />
                  <span className="text-lg font-semibold text-gray-900">Built-in provider network and fulfillment team</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-6 h-6 text-teal-600" />
                  <span className="text-lg font-semibold text-gray-900">HIPAA-compliant tech and prescription workflows</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-6 h-6 text-teal-600" />
                  <span className="text-lg font-semibold text-gray-900">Proven onboarding and monetization systems</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-6 h-6 text-teal-600" />
                  <span className="text-lg font-semibold text-gray-900">No billing insurance, no red tape, no medical license needed</span>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 60 }}
              animate={isVisible.problem ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              {/* Updated laptop with exact Uniloop dashboard - Better Proportions */}
              <div className="w-full max-w-2xl mx-auto">
                {/* Laptop Frame */}
                <div className="relative bg-gray-800 rounded-t-2xl p-2 md:p-4">
                  <div className="bg-gray-900 rounded-2xl p-4 md:p-6 shadow-2xl">
                    {/* Screen Content */}
                    <div className="bg-white rounded-xl p-4 md:p-6 aspect-[4/3] overflow-hidden">
                      {/* Uniloop Dashboard */}
                      <div className="h-full flex flex-col">
                        {/* Header */}
                        <div className="mb-4 flex-shrink-0">
                          <h1 className="text-lg md:text-2xl font-bold text-gray-900 mb-1">Welcome back!</h1>
                          <p className="text-gray-500 text-xs md:text-sm">Here's an overview of your health and treatment progress.</p>
                        </div>
                        
                        {/* Stats Cards */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 mb-4 flex-shrink-0">
                          <div className="bg-blue-50 p-2 md:p-3 rounded-lg">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs text-gray-600">Active</span>
                              <div className="w-2 h-2 bg-blue-500 rounded"></div>
                            </div>
                            <div className="text-sm md:text-xl font-bold text-gray-900">2</div>
                            <div className="text-xs text-gray-500">treatments</div>
                          </div>
                          
                          <div className="bg-green-50 p-2 md:p-3 rounded-lg">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs text-gray-600">Orders</span>
                              <div className="w-2 h-2 bg-green-500 rounded"></div>
                            </div>
                            <div className="text-sm md:text-xl font-bold text-gray-900">3</div>
                            <div className="text-xs text-gray-500">recent</div>
                          </div>
                          
                          <div className="bg-purple-50 p-2 md:p-3 rounded-lg">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs text-gray-600">Subs</span>
                              <div className="w-2 h-2 bg-purple-500 rounded"></div>
                            </div>
                            <div className="text-sm md:text-xl font-bold text-gray-900">2</div>
                            <div className="text-xs text-gray-500">active</div>
                          </div>
                          
                          <div className="bg-orange-50 p-2 md:p-3 rounded-lg">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs text-gray-600">Progress</span>
                              <div className="w-2 h-2 bg-orange-500 rounded"></div>
                            </div>
                            <div className="text-sm md:text-xl font-bold text-gray-900">0/1</div>
                            <div className="text-xs text-gray-500">today</div>
                          </div>
                        </div>
                        
                        {/* Bottom Section */}
                        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <div className="flex items-center mb-2">
                              <div className="w-3 h-3 bg-gray-800 rounded mr-2"></div>
                              <span className="font-semibold text-gray-900 text-xs">Today's Schedule</span>
                            </div>
                            <div className="space-y-1">
                              <div className="flex items-center">
                                <div className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></div>
                                <span className="text-xs font-medium">Medication</span>
                              </div>
                              <div className="text-xs text-gray-600">500 mg</div>
                              <span className="inline-block bg-green-100 text-green-700 text-xs px-2 py-1 rounded">pending</span>
                            </div>
                          </div>
                          
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <span className="font-semibold text-gray-900 text-xs">Quick Actions</span>
                            <div className="space-y-1 mt-2">
                              <div className="bg-blue-500 text-white text-xs px-2 py-1 rounded">Browse</div>
                              <div className="bg-gray-200 text-gray-700 text-xs px-2 py-1 rounded">Prescriptions</div>
                              <div className="bg-gray-200 text-gray-700 text-xs px-2 py-1 rounded">History</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Laptop Base */}
                <div className="bg-gray-300 h-3 md:h-4 rounded-b-2xl mx-auto" style={{width: '60%'}}></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section id="included" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 60 }}
            animate={isVisible.included ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              What's Included in Your
              <span className="block bg-gradient-to-r from-teal-600 to-teal-700 bg-clip-text text-transparent">
                Turnkey Clinic
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              You own the brand. You control the revenue. We handle the medicine.
            </p>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={staggerContainer}
            initial="initial"
            animate={isVisible.included ? "animate" : "initial"}
          >
            {includedFeatures.map((feature, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-slate-200 hover:border-teal-200 group"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <feature.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 60 }}
            animate={isVisible.services ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Services You Can Offer
              <span className="block bg-gradient-to-r from-teal-600 to-teal-700 bg-clip-text text-transparent">
                From Day One
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              You can specialize or offer a broad menu. We've got providers ready for all of it.
            </p>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={staggerContainer}
            initial="initial"
            animate={isVisible.services ? "animate" : "initial"}
          >
            {services.map((service, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="group relative bg-gradient-to-br from-white to-slate-50 rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-slate-200 hover:border-teal-200 overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br opacity-10 rounded-full transform translate-x-16 -translate-y-16 group-hover:scale-150 transition-transform duration-700" 
                     style={{backgroundImage: `linear-gradient(135deg, ${service.color.split(' ')[1]}, ${service.color.split(' ')[3]})`}} />
                
                <div className="relative z-10">
                  <div className={`w-16 h-16 bg-gradient-to-br ${service.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <service.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {service.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ROI Calculator Section - Moved after Services */}
      <section id="roi-calculator" className="py-20 bg-gradient-to-br from-slate-100 to-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 60 }}
            animate={isVisible['roi-calculator'] ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Calculate Your
              <span className="block bg-gradient-to-r from-teal-600 to-teal-700 bg-clip-text text-transparent">
                Partnership ROI
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See your potential revenue projections with our interactive calculator based on industry data
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={isVisible['roi-calculator'] ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <ROICalculator />
          </motion.div>
        </div>
      </section>

      {/* Target Audience Section */}
      <section id="audience" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 60 }}
            animate={isVisible.audience ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Who This Is
              <span className="block bg-gradient-to-r from-teal-600 to-teal-700 bg-clip-text text-transparent">
                Perfect For
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              This opportunity is perfect for ambitious individuals ready to enter the telehealth market
            </p>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
            variants={staggerContainer}
            initial="initial"
            animate={isVisible.audience ? "animate" : "initial"}
          >
            {targetAudience.map((audience, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-slate-200 hover:border-teal-200 group text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 mx-auto">
                  <audience.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {audience.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {audience.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Market Opportunity Section */}
      <section id="market" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 60 }}
            animate={isVisible.market ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Why Telehealth?
              <span className="block bg-gradient-to-r from-teal-600 to-teal-700 bg-clip-text text-transparent">
                Why Now?
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The $300B+ telehealth market is growing rapidly — especially in cash-pay verticals 
              like weight loss, men's health, and HRT.
            </p>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-4 gap-8 mb-16"
            variants={staggerContainer}
            initial="initial"
            animate={isVisible.market ? "animate" : "initial"}
          >
            {marketStats.map((stat, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="text-center"
              >
                <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-teal-600 to-teal-700 bg-clip-text text-transparent mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 font-semibold">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-5 gap-6"
            variants={staggerContainer}
            initial="initial"
            animate={isVisible.market ? "animate" : "initial"}
          >
            {[
              { icon: DollarSign, text: "No insurance billing" },
              { icon: TrendingUp, text: "High customer lifetime value" },
              { icon: Building, text: "Low overhead, high retention" },
              { icon: Globe, text: "Remote from day one" },
              { icon: Clock, text: "24/7 fulfillment team access" }
            ].map((item, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="flex flex-col items-center text-center p-6 bg-white rounded-2xl hover:bg-teal-50 transition-colors duration-300 shadow-lg"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-white" />
                </div>
                <span className="font-semibold text-gray-900">{item.text}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="cta" className="py-20 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://images.pexels.com/photos/5452293/pexels-photo-5452293.jpeg')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 to-gray-800/85" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={isVisible.cta ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Apply to Partner
              <span className="block bg-gradient-to-r from-teal-300 to-teal-100 bg-clip-text text-transparent">
                Today
              </span>
            </h2>
            <p className="text-xl text-gray-200 mb-8 max-w-3xl mx-auto">
              This is not a franchise. This is your brand, your business — with our engine behind it.
              Skip the 6–12 months of licensing, hiring, and setup.
            </p>
            <div className="bg-teal-100 text-teal-800 p-6 rounded-2xl mb-8 max-w-2xl mx-auto">
              <p className="text-lg font-semibold">
                We'll hand you the keys on how to build a fully functional telehealth business — and help you grow it.
              </p>
            </div>
            <motion.button
              onClick={handleCalendlyLink}
              className="bg-gradient-to-r from-teal-500 to-teal-600 text-white px-12 py-5 rounded-full font-bold text-xl hover:from-teal-600 hover:to-teal-700 transform hover:scale-105 transition-all duration-300 shadow-2xl hover:shadow-3xl flex items-center space-x-3 mx-auto"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <MessageSquare className="w-6 h-6" />
              <span>Apply to Partner with TeleTurn Key</span>
            </motion.button>
            <p className="text-gray-400 mt-4">
              Partnership inquiry • Complete application • Personalized consultation
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 60 }}
            animate={isVisible.faq ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Frequently Asked
              <span className="block bg-gradient-to-r from-teal-600 to-teal-700 bg-clip-text text-transparent">
                Questions
              </span>
            </h2>
            <p className="text-xl text-gray-600">
              Get answers to common questions about our partnership opportunity
            </p>
          </motion.div>

          <motion.div
            className="space-y-4"
            variants={staggerContainer}
            initial="initial"
            animate={isVisible.faq ? "animate" : "initial"}
          >
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-white rounded-2xl shadow-md overflow-hidden border border-slate-200 hover:border-teal-200 transition-all duration-300"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-8 py-6 text-left flex justify-between items-center hover:bg-teal-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900 text-lg">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-teal-600" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-teal-600" />
                  )}
                </button>
                {openFaq === index && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="px-8 pb-6 text-gray-600 leading-relaxed"
                  >
                    {faq.answer}
                  </motion.div>
                )}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section - Moved to last section before footer */}
      <section id="testimonials" className="py-20 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://images.pexels.com/photos/5407220/pexels-photo-5407220.jpeg')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-teal-900/95 to-teal-800/90" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 60 }}
            animate={isVisible.testimonials ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              What Our
              <span className="block text-teal-200">Partners Say</span>
            </h2>
            <p className="text-xl text-teal-100 max-w-3xl mx-auto">
              Real results from real partners who've transformed their businesses
            </p>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-3 gap-8"
            variants={staggerContainer}
            initial="initial"
            animate={isVisible.testimonials ? "animate" : "initial"}
          >
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-white/10 backdrop-blur-md rounded-3xl p-8 border border-white/20 hover:bg-white/20 transition-all duration-300"
              >
                <div className="flex items-center mb-6">
                  <img 
                    src={testimonial.avatar} 
                    alt={testimonial.name}
                    className="w-16 h-16 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h4 className="text-white font-semibold text-lg">{testimonial.name}</h4>
                    <p className="text-teal-200">{testimonial.role}</p>
                  </div>
                </div>
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-white/90 italic leading-relaxed text-lg">
                  "{testimonial.content}"
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center">
                  <span className="text-white font-bold text-sm">TTK</span>
                </div>
                <span className="text-2xl font-bold">TeleTurn Key</span>
              </div>
              <p className="text-gray-400 leading-relaxed">
                Empowering entrepreneurs to build successful telehealth businesses with our complete partnership platform.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-lg mb-4">Partnership</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Apply to Partner</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-lg mb-4">Services</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Weight Loss</li>
                <li>Men's Health</li>
                <li>Hormone Therapy</li>
                <li>Mental Health</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-lg mb-4">Contact</h4>
              <div className="space-y-2 text-gray-400">
                <div className="flex items-center space-x-2">
                  <Mail className="w-4 h-4" />
                  <span>contact@teleturnkey.com</span>
                </div>
                <div className="flex items-center space-x-2">
                  <MapPin className="w-4 h-4" />
                  <span>San Diego, CA</span>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 TeleTurn Key. All rights reserved.</p>
            <p className="text-xs mt-2">Unauthorized reproduction or distribution of this content is strictly prohibited.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

function App() {
  return <TeleTurnKeyPartnership />;
}

export default App;